import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AcceptAdvisorInvitationRequest } from '../../models/Advisor';
import {
  submitAdvisorInvitationAcceptance,
  validateInvitationForm,
} from './acceptInvitationSubmission';

type FormErrors = {
  firstName?: string;
  lastName?: string;
  mobile?: string;
};

export function useAcceptInvitationSubmission(input: {
  invitationId?: string;
  isExistingContact: boolean;
  prefilledFirstName: string;
  prefilledLastName: string;
  setError: (message: string | null) => void;
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<AcceptAdvisorInvitationRequest>({
    firstName: input.prefilledFirstName,
    lastName: input.prefilledLastName,
    mobile: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (input.prefilledFirstName || input.prefilledLastName) {
      setFormData((prev) => ({
        ...prev,
        firstName: input.prefilledFirstName,
        lastName: input.prefilledLastName,
      }));
    }
  }, [input.prefilledFirstName, input.prefilledLastName]);

  const handleInputChange = (field: keyof AcceptAdvisorInvitationRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAccept = async () => {
    const errors = validateInvitationForm(input.isExistingContact, formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0 || !input.invitationId) {
      return;
    }

    setSubmitting(true);
    input.setError(null);
    const result = await submitAdvisorInvitationAcceptance({
      invitationId: input.invitationId,
      isExistingContact: input.isExistingContact,
      formData,
    });
    setSubmitting(false);

    if (result.success) {
      setSuccess(true);
      return;
    }
    input.setError(result.message);
  };

  const isAcceptDisabled =
    submitting ||
    (!input.isExistingContact &&
      (!formData.firstName.trim() || !formData.lastName.trim() || !formData.mobile.trim()));

  return {
    submitting,
    success,
    formData,
    formErrors,
    handleInputChange,
    handleAccept,
    handleReject: () => navigate(`/reject-invitation/${input.invitationId}`),
    goHome: () => navigate('/'),
    isAcceptDisabled,
  };
}
